import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import Logo from '../components/Logo';
import { ActiveCompanyProvider, useActiveCompany } from './state';
import { CurrentUserProvider } from './CurrentUserProvider';
import { getOnboardingState } from '../lib/entities/onboarding';
import { docStats } from './data';
import {
  Bubble,
  Buildings,
  Cross,
  Doc,
  Folder,
  Gear,
  Grid,
  Person,
} from './components/AppIcons';
import {
  dismissNotification,
  listNotifications,
  markNotificationClicked,
  type NotificationDto,
} from '../lib/entities/notifications';
import { formatDate } from '../lib/entities/format';
import { prefetchDocxEditor } from './components/docxEditor';
import '../styles/app.css';

/** Bell, in the AppIcons hand (24-box, 1.8 stroke, round caps, currentColor). */
function Bell({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 4a5.5 5.5 0 00-5.5 5.5c0 3.6-1 5-1.9 5.9-.35.36-.1 1.1.4 1.1h14c.5 0 .75-.74.4-1.1-.9-.9-1.9-2.3-1.9-5.9A5.5 5.5 0 0012 4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M10 19.4a2 2 0 004 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Notifications bell: badge = un-clicked count, dropdown lists the user's
 * notifications (open marks clicked, "x" dismisses). Loads once on mount —
 * deliberately no polling loop, the shell stays quiet.
 */
function NotificationsBell() {
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [newCount, setNewCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    listNotifications()
      .then((res) => {
        if (cancelled) return;
        setItems(res.notifications);
        setNewCount(res.newCount);
      })
      .catch(() => {
        // Quiet failure: the bell just shows nothing rather than breaking the shell.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onOpenNotification = async (n: NotificationDto) => {
    if (n.clickedAt) return;
    try {
      const updated = await markNotificationClicked(n.id);
      setItems((prev) => prev.map((x) => (x.id === n.id ? updated : x)));
      setNewCount((c) => Math.max(0, c - 1));
    } catch {
      // Leave it marked new; the next load reconciles.
    }
  };

  const onDismiss = async (n: NotificationDto) => {
    try {
      await dismissNotification(n.id);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      if (!n.clickedAt) setNewCount((c) => Math.max(0, c - 1));
    } catch {
      // Keep the row; the next load reconciles.
    }
  };

  return (
    <div className="bell" ref={ref}>
      <button
        type="button"
        className="bell__btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={newCount > 0 ? `Notifications (${newCount} new)` : 'Notifications'}
      >
        <Bell />
        {newCount > 0 && <span className="bell__badge">{newCount}</span>}
      </button>

      {open && (
        <div className="bell__menu" role="menu" aria-label="Notifications">
          <div className="bell__head">Notifications</div>
          {items.length === 0 ? (
            <div className="bell__empty">You’re all caught up.</div>
          ) : (
            items.map((n) => (
              <div key={n.id} className={`bell__item${n.clickedAt ? '' : ' is-new'}`}>
                <button
                  type="button"
                  className="bell__body"
                  onClick={() => void onOpenNotification(n)}
                >
                  <span className="bell__msg">{n.message}</span>
                  {n.note && <span className="bell__note">{n.note}</span>}
                  <span className="bell__when">{formatDate(n.createdAt)}</span>
                </button>
                <button
                  type="button"
                  className="bell__dismiss"
                  aria-label="Dismiss notification"
                  onClick={() => void onDismiss(n)}
                >
                  <Cross size={13} />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Clerk account controls: organization (= tenant) switcher + user/sign-out menu.
 * Relies on the <ClerkProvider> that AppAuthGate mounts around this layout (see
 * AppAuth.tsx / routes.tsx).
 */
/**
 * Sign out — a plain text control. Single-company mode has no account-management UI yet (no Clerk
 * UserButton / avatar), so this is the only session control in the shell. Clerk's afterSignOutUrl
 * ("/") is set on the provider, and we pass redirectUrl too so sign-out always lands on the site.
 */
function SignOutControl({ onSignOut }: { onSignOut?: () => void }) {
  const { signOut } = useClerk();
  return (
    <button
      type="button"
      className="app-sidebar__signout"
      onClick={() => {
        onSignOut?.();
        void signOut({ redirectUrl: '/' });
      }}
    >
      Sign out
    </button>
  );
}

/**
 * The active company chip. In single-company mode there's nothing to switch
 * between, so instead of a switcher it's a link into the company config page.
 * The loading / no-company state stays a plain, non-interactive placeholder.
 */
function CompanySwitcher({ onNavigate }: { onNavigate: () => void }) {
  const { company, loading } = useActiveCompany();

  if (!company) {
    return (
      <div className="switcher">
        <div className="switcher__btn" aria-hidden="true">
          <span className="switcher__mark">·</span>
          <span className="switcher__copy">
            <span className="switcher__name">{loading ? 'Loading…' : 'No company yet'}</span>
            <span className="switcher__meta">
              {loading ? 'Fetching your company' : 'Finish onboarding to get started'}
            </span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="switcher">
      <Link to="/app/company" className="switcher__btn" onClick={onNavigate}>
        <span className="switcher__mark">{company.name.charAt(0)}</span>
        <span className="switcher__copy">
          <span className="switcher__name">{company.name}</span>
          <span className="switcher__meta">
            {company.employees} {company.employees === 1 ? 'employee' : 'employees'}
          </span>
        </span>
      </Link>
    </div>
  );
}

function SidebarNav({ onNavigate }: { onNavigate: () => void }) {
  const { documents, employees, cases } = useActiveCompany();
  const stats = docStats(documents);
  const openCases = cases.filter((c) => c.status !== 'resolved').length;
  const employeeActions = employees.filter(
    (e) => e.checklist.outstanding > 0 || e.checklist.needsAnswer > 0,
  ).length;

  const items = [
    { to: '/app', end: true, label: 'Overview', icon: <Grid size={19} /> },
    { to: '/app/assistant', label: 'Assistant', icon: <Bubble size={19} /> },
    {
      to: '/app/documents',
      // "Requirements" is what the page is: everything a business like this one has to have, of which
      // documents are only part — the attestations on it are not documents at all. The route keeps its
      // /documents path so existing links and bookmarks still work.
      label: 'Requirements',
      icon: <Doc size={19} />,
      badge: stats.needsAttention || undefined,
    },
    {
      to: '/app/employees',
      label: 'Employees',
      icon: <Person size={19} />,
      badge: employeeActions || undefined,
    },
    { to: '/app/cases', label: 'Cases', icon: <Folder size={19} />, badge: openCases || undefined },
  ];

  return (
    <nav className="anav" aria-label="App">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) => `anav__item${isActive ? ' is-active' : ''}`}
          onClick={onNavigate}
        >
          {item.icon}
          <span className="anav__label">{item.label}</span>
          {item.badge !== undefined && <span className="anav__badge">{item.badge}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function Shell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // New page → scroll the content pane back to the top.
  useEffect(() => {
    document.querySelector('.app-main')?.scrollTo({ top: 0 });
  }, [pathname]);

  // Warm the heavy document-editor bundle in the background while the user is elsewhere in the app, so the
  // first document open is instant. Idle-time, once, memoized, and skipped on data-saver connections.
  useEffect(() => {
    prefetchDocxEditor();
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="app-shell">
      {/* Small-screen top bar */}
      <header className="app-topbar">
        <Link to="/app" aria-label="reeve overview">
          <Logo height={26} />
        </Link>
        <button
          type="button"
          className="app-topbar__menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? <Cross size={20} /> : <span className="app-topbar__burger" aria-hidden="true" />}
          Menu
        </button>
      </header>

      {menuOpen && <div className="app-scrim" onClick={closeMenu} />}

      <aside className={`app-sidebar${menuOpen ? ' is-open' : ''}`}>
        <div className="app-sidebar__top">
          <Link to="/app" className="app-sidebar__logo" aria-label="reeve overview" onClick={closeMenu}>
            <Logo height={28} />
          </Link>
          <NotificationsBell />
        </div>

        <CompanySwitcher onNavigate={closeMenu} />
        <SidebarNav onNavigate={closeMenu} />

        <div className="app-sidebar__foot">
          <NavLink
            to="/app/company"
            className={({ isActive }) => `anav__item anav__item--quiet${isActive ? ' is-active' : ''}`}
            onClick={closeMenu}
          >
            <Buildings size={19} />
            <span className="anav__label">Company</span>
          </NavLink>
          <NavLink
            to="/app/settings"
            className={({ isActive }) => `anav__item anav__item--quiet${isActive ? ' is-active' : ''}`}
            onClick={closeMenu}
          >
            <Gear size={19} />
            <span className="anav__label">Settings</span>
          </NavLink>
          <SignOutControl onSignOut={closeMenu} />
          <Link to="/" className="app-sidebar__site" onClick={closeMenu}>
            ← Back to reeve.hr
          </Link>
        </div>
      </aside>

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Redirect to the guided onboarding wizard until the user is fully set up
 * (profile + a company + that company's findings gathered). /app/onboarding is a
 * SEPARATE route element (not this layout), so the redirect can't loop back here.
 *
 * On a state-fetch error we render the app anyway rather than trap the user out. That is safe
 * because ConnectionGate (in AppAuthGate) has already established that the backend answers, so an
 * error here is a real one-off rather than an outage. Before that gate existed, this catch was half
 * of why a dead backend looked like an empty account: it fell through to the shell, which then
 * offered to set up a company the user already had.
 */
function OnboardingGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'redirect'>('loading');

  useEffect(() => {
    let cancelled = false;
    getOnboardingState()
      .then((state) => {
        if (!cancelled) setStatus(state.complete ? 'ready' : 'redirect');
      })
      .catch(() => {
        if (!cancelled) setStatus('ready');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'loading') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 16px',
          color: 'var(--ink-muted)',
        }}
      >
        Loading…
      </div>
    );
  }
  if (status === 'redirect') return <Navigate to="/app/onboarding" replace />;
  return <>{children}</>;
}

/** The authenticated app shell: sidebar, company context, content pane. */
export default function AppLayout() {
  return (
    <OnboardingGate>
      <CurrentUserProvider>
        <ActiveCompanyProvider>
          <Shell />
        </ActiveCompanyProvider>
      </CurrentUserProvider>
    </OnboardingGate>
  );
}
